/**
 * Recipe costing for production units, kept free of the database so it can be
 * tested on its own (api/test/recipeCost.test.mjs).
 *
 * The same file is in api/src/libs/recipeCost.ts (only the DMath import
 * differs); change both together.
 *
 * A production unit is a recipe: it makes `quantity` of the product's base
 * unit from its materials and `labourCost` (per batch). Each material line is
 *  - `quantity` in `unit`: the material's base unit, or one of its sub-units
 *    (a sub-unit's quantity is how many base units it holds, so "g" on a "Kg"
 *    product is 0.001),
 *  - `wastePct`: the part lost in preparation (peel, trim, spill). The line's
 *    quantity is what goes into the recipe (net); what is used up, costed and
 *    taken from stock is net / (1 - waste%) (gross).
 *
 * A material's cost per base unit is the location's cost (the branch's or
 * store's, else the product's), passed in as `unitCost`. A material that has
 * its own production unit (a sub-recipe, e.g. dough) is costed from its
 * default production unit instead, with its material and labour parts kept
 * apart, so food cost counts only materials at every level.
 */

import { DMath, DecimalLike } from "./DMath";

export interface CostUnit {
  name: string;
  quantity: string;
}

export interface CostMaterial {
  id: string;
  quantity: string;
  unit?: string;
  wastePct?: string;
}

export interface CostPUnit {
  name: string;
  quantity: string;
  labourCost: string;
  isDefault?: boolean;
  materials: Array<CostMaterial>;
}

export interface CostProduct {
  id: string;
  name?: string;
  unit?: string;
  isService?: boolean;
  units?: Array<CostUnit>;
  pUnits?: Array<CostPUnit>;
  /** The location's cost per base unit. */
  unitCost: DecimalLike;
}

export type CostStatus = "good" | "warning" | "danger" | "none";

export interface CostLine {
  id: string;
  name: string;
  /** The unit the line's quantity is in. */
  unit: string;
  quantity: string;
  wastePct: string;
  /** Base units used up per batch, waste included. */
  grossBaseQty: string;
  /** Material and labour cost per base unit of the material. */
  unitMaterialCost: string;
  unitLabourCost: string;
  /** Cost of the line per batch. */
  materialCost: string;
  labourCost: string;
  cost: string;
  /** Cost of the waste part of the line. */
  wasteCost: string;
  /** Share of the batch's material cost, 0-100. */
  share: string;
  isSubRecipe: boolean;
  /** The material is not in `products`, or its unit no longer exists. */
  isMissing: boolean;
  isUnitMissing: boolean;
}

export interface PUnitCost {
  name: string;
  yieldQty: string;
  lines: Array<CostLine>;
  /** Per batch. */
  materialCost: string;
  /** Per batch: the production unit's labour plus that of its sub-recipes. */
  labourCost: string;
  wasteCost: string;
  totalCost: string;
  /** Per base unit of the product. */
  materialCostPerUnit: string;
  labourCostPerUnit: string;
  costPerUnit: string;
  hasMissing: boolean;
}

export interface CostSummary {
  netPrice: string;
  /** Material cost / net price, 0-100+. */
  foodCostPct: string;
  /** (Material + labour) cost / net price. */
  primeCostPct: string;
  grossProfit: string;
  grossProfitPct: string;
  /** Net price that brings food cost to the target. */
  suggestedPrice: string;
  status: CostStatus;
}

export const MAX_RECIPE_DEPTH = 5;
export const DEFAULT_TARGET_FOOD_COST = "30";
export const DEFAULT_MAX_FOOD_COST = "35";

const isPositive = (value: any) =>
  value !== null &&
  value !== undefined &&
  value !== "" &&
  isFinite(Number(value)) &&
  DMath.gt(value, 0);

const valueOr = (value: any, fallback: DecimalLike) =>
  value === null || value === undefined || value === "" || !isFinite(Number(value))
    ? fallback
    : value;

/** The production unit used when one has to be chosen: the default one, else the first. */
export function defaultPUnit<T extends CostPUnit>(
  pUnits: Array<T> | undefined | null,
): T | null {
  if (!pUnits || pUnits.length < 1) return null;
  return pUnits.find((pu) => pu?.isDefault === true) || pUnits[0] || null;
}

/** How many base units one `unit` of the product holds, or null if the product has no such unit. */
export function unitFactor(
  product: CostProduct | undefined | null,
  unit: string | undefined | null,
): string | null {
  if (!unit || !product || unit === product.unit) return "1";
  const u = product.units?.find((un) => un?.name === unit);
  return u && isPositive(u.quantity) ? DMath.toDecimal(u.quantity).toString() : null;
}

/** Waste % as a number in [0, 100), 0 when missing or out of range. */
export function wasteOf(material: CostMaterial): string {
  const w = valueOr(material?.wastePct, 0);
  if (DMath.lt(w, 0) || DMath.gte(w, 100)) return "0";
  return DMath.toDecimal(w).toString();
}

/**
 * Base units of the material used up per batch: the line's quantity in base
 * units, plus the waste. Null when the line's unit no longer exists.
 */
export function grossBaseQty(
  material: CostMaterial,
  product: CostProduct | undefined | null,
): string | null {
  const factor = unitFactor(product, material?.unit);
  if (factor === null) return null;
  const net = DMath.mul(valueOr(material?.quantity, 0), factor);
  const keep = DMath.sub(1, DMath.div(wasteOf(material), 100));
  return DMath.div(net, keep).toString();
}

const hasRecipe = (p: CostProduct | undefined) =>
  !!p && !p.isService && !!defaultPUnit(p.pUnits)?.materials?.length;

/**
 * Material and labour cost per base unit of a product: from its default
 * production unit when it has one (and the depth or a loop does not stop it),
 * else the location's cost, all of it material.
 */
export function productUnitCost(
  productId: string,
  products: Array<CostProduct>,
  depth = 0,
  path: Array<string> = [],
): { material: string; labour: string; isSubRecipe: boolean } {
  const product = products.find((p) => p.id === productId);
  if (!product) return { material: "0", labour: "0", isSubRecipe: false };
  if (
    hasRecipe(product) &&
    depth < MAX_RECIPE_DEPTH &&
    path.indexOf(productId) < 0
  ) {
    const pu = defaultPUnit(product.pUnits)!;
    if (isPositive(pu.quantity)) {
      const cost = costPUnit(pu, products, depth + 1, [...path, productId]);
      if (!cost.hasMissing) {
        return {
          material: cost.materialCostPerUnit,
          labour: cost.labourCostPerUnit,
          isSubRecipe: true,
        };
      }
    }
  }
  return {
    material: DMath.toDecimal(valueOr(product.unitCost, 0)).toString(),
    labour: "0",
    isSubRecipe: false,
  };
}

/** Cost of one batch of a production unit, and per base unit of the product. */
export function costPUnit(
  pUnit: CostPUnit,
  products: Array<CostProduct>,
  depth = 0,
  path: Array<string> = [],
): PUnitCost {
  let materialCost = DMath.toDecimal(0);
  let subLabour = DMath.toDecimal(0);
  let wasteCost = DMath.toDecimal(0);
  let hasMissing = false;

  const raw = (pUnit?.materials || [])
    .filter((m) => m && m.id)
    .map((m) => {
      const product = products.find((p) => p.id === m.id);
      const gross = grossBaseQty(m, product);
      const isUnitMissing = !!product && gross === null;
      const isMissing = !product || isUnitMissing;
      if (isMissing) hasMissing = true;
      const unit = productUnitCost(m.id, products, depth, path);
      const qty = gross === null ? DMath.toDecimal(0) : DMath.toDecimal(gross);
      const lineMaterial = DMath.mul(qty, unit.material);
      const lineLabour = DMath.mul(qty, unit.labour);
      const waste = wasteOf(m);
      const lineWaste = DMath.gt(waste, 0)
        ? DMath.mul(lineMaterial, DMath.div(waste, 100))
        : DMath.toDecimal(0);
      materialCost = DMath.add(materialCost, lineMaterial);
      subLabour = DMath.add(subLabour, lineLabour);
      wasteCost = DMath.add(wasteCost, lineWaste);
      return {
        m,
        product,
        qty,
        unit,
        lineMaterial,
        lineLabour,
        lineWaste,
        isMissing,
        isUnitMissing,
      };
    });

  const lines: Array<CostLine> = raw.map((r) => ({
    id: r.m.id,
    name: r.product?.name || "",
    unit: r.m.unit || r.product?.unit || "",
    quantity: DMath.toDecimal(valueOr(r.m.quantity, 0)).toString(),
    wastePct: wasteOf(r.m),
    grossBaseQty: r.qty.toString(),
    unitMaterialCost: r.unit.material,
    unitLabourCost: r.unit.labour,
    materialCost: r.lineMaterial.toString(),
    labourCost: r.lineLabour.toString(),
    cost: DMath.add(r.lineMaterial, r.lineLabour).toString(),
    wasteCost: r.lineWaste.toString(),
    share: DMath.gt(materialCost, 0)
      ? DMath.mul(DMath.div(r.lineMaterial, materialCost), 100).toString()
      : "0",
    isSubRecipe: r.unit.isSubRecipe,
    isMissing: r.isMissing,
    isUnitMissing: r.isUnitMissing,
  }));

  const labourCost = DMath.add(valueOr(pUnit?.labourCost, 0), subLabour);
  const totalCost = DMath.add(materialCost, labourCost);
  const yieldQty = valueOr(pUnit?.quantity, 0);
  const perUnit = (v: DecimalLike) =>
    isPositive(yieldQty) ? DMath.div(v, yieldQty).toString() : "0";

  return {
    name: pUnit?.name || "",
    yieldQty: DMath.toDecimal(yieldQty).toString(),
    lines,
    materialCost: materialCost.toString(),
    labourCost: labourCost.toString(),
    wasteCost: wasteCost.toString(),
    totalCost: totalCost.toString(),
    materialCostPerUnit: perUnit(materialCost),
    labourCostPerUnit: perUnit(labourCost),
    costPerUnit: perUnit(totalCost),
    hasMissing,
  };
}

/**
 * The selling price without tax. A price that includes tax holds
 * price x tax / (100 + tax) of it.
 */
export function netSellingPrice(
  price: DecimalLike,
  taxPercentage: DecimalLike,
  isTaxIncludedInPrice: boolean,
): string {
  const p = valueOr(price, 0);
  const tax = valueOr(taxPercentage, 0);
  if (!isTaxIncludedInPrice || !DMath.gt(tax, 0)) {
    return DMath.toDecimal(p).toString();
  }
  return DMath.div(DMath.mul(p, 100), DMath.add(100, tax)).toString();
}

/** The listed price for a net price: tax added back when prices include it. */
export function listedSellingPrice(
  netPrice: DecimalLike,
  taxPercentage: DecimalLike,
  isTaxIncludedInPrice: boolean,
): string {
  const p = valueOr(netPrice, 0);
  const tax = valueOr(taxPercentage, 0);
  if (!isTaxIncludedInPrice || !DMath.gt(tax, 0)) {
    return DMath.toDecimal(p).toString();
  }
  return DMath.div(DMath.mul(p, DMath.add(100, tax)), 100).toString();
}

/** Food cost status against the target and the warning level. */
export function costStatus(
  foodCostPct: DecimalLike,
  target: DecimalLike,
  max: DecimalLike,
  hasPrice = true,
): CostStatus {
  if (!hasPrice) return "none";
  if (DMath.lte(foodCostPct, valueOr(target, DEFAULT_TARGET_FOOD_COST)))
    return "good";
  if (DMath.lte(foodCostPct, valueOr(max, DEFAULT_MAX_FOOD_COST)))
    return "warning";
  return "danger";
}

/**
 * Food cost %, prime cost %, gross profit and the price that meets the target,
 * for a cost per base unit against the net selling price per base unit.
 */
export function costSummary(
  materialCostPerUnit: DecimalLike,
  costPerUnit: DecimalLike,
  netPrice: DecimalLike,
  target: DecimalLike = DEFAULT_TARGET_FOOD_COST,
  max: DecimalLike = DEFAULT_MAX_FOOD_COST,
): CostSummary {
  const price = valueOr(netPrice, 0);
  const hasPrice = isPositive(price);
  const pct = (v: DecimalLike) =>
    hasPrice ? DMath.mul(DMath.div(v, price), 100).toString() : "0";
  const foodCostPct = pct(materialCostPerUnit);
  const grossProfit = DMath.sub(price, valueOr(costPerUnit, 0));
  const t = valueOr(target, DEFAULT_TARGET_FOOD_COST);
  return {
    netPrice: DMath.toDecimal(price).toString(),
    foodCostPct,
    primeCostPct: pct(costPerUnit),
    grossProfit: grossProfit.toString(),
    grossProfitPct: pct(grossProfit),
    suggestedPrice: isPositive(t)
      ? DMath.div(DMath.mul(valueOr(materialCostPerUnit, 0), 100), t).toString()
      : "0",
    status: costStatus(foodCostPct, t, max, hasPrice),
  };
}

/**
 * Base units of each material a production uses up: `count` batches of the
 * production unit, waste included, rounded to 3 decimals as stock is kept.
 * Only the production unit's own materials: a sub-recipe is taken from its
 * stock, not made again. Null when a line's unit no longer exists.
 */
export function consumedMaterials(
  pUnit: CostPUnit,
  products: Array<CostProduct>,
  count: DecimalLike,
): Array<{ id: string; quantity: string }> | null {
  const byId = new Map<string, any>();
  for (const m of pUnit?.materials || []) {
    if (!m || !m.id) continue;
    const gross = grossBaseQty(
      m,
      products.find((p) => p.id === m.id),
    );
    if (gross === null) return null;
    byId.set(
      m.id,
      DMath.add(byId.get(m.id) || 0, DMath.mul(gross, valueOr(count, 0))),
    );
  }
  return Array.from(byId.entries()).map(([id, qty]) => ({
    id,
    quantity: DMath.toFixed(qty, 3),
  }));
}

/**
 * The stock's cost after adding `addQty` at `addCost` to `stockQty` at
 * `stockCost`, per base unit (2 decimals, as the location keeps it). Stock at
 * or below zero has no value to average with, so the new cost is taken.
 */
export function weightedAverageCost(
  stockQty: DecimalLike,
  stockCost: DecimalLike,
  addQty: DecimalLike,
  addCost: DecimalLike,
): string {
  const q0 = valueOr(stockQty, 0);
  const q1 = valueOr(addQty, 0);
  if (!DMath.gt(q0, 0)) return DMath.toFixed(valueOr(addCost, 0));
  if (!DMath.gt(q1, 0)) return DMath.toFixed(valueOr(stockCost, 0));
  const value = DMath.add(
    DMath.mul(q0, valueOr(stockCost, 0)),
    DMath.mul(q1, valueOr(addCost, 0)),
  );
  return DMath.toFixed(DMath.div(value, DMath.add(q0, q1)));
}
